
import { i18nManager } from "../i18n";
import { CacheManager, LzEncryptor, Utils } from "../utils";
import { AlertInterface, NoseurObject, TypeChecker } from "@ronuse/noseur";
import ffs, { Ffs, HttpConfig, IndexDbCacheManager, Interceptor, CacheManager as KCacheManager, SessionStorageCacheManager } from "kyofuuc";

export interface IProfile {
    email: string;
    last_name: string;
    first_name: string;
    other_names: string;
    external_id: string;
    profile_picture_location: string;
}

export interface ApiResponseData<T> {
    data?: T;
    content?: T[];
    size?: number;
    transformed?: any;
    total_pages?: number;
    total_elements?: number;
    number_of_elements?: number;
    pageable?: {
        paged: boolean;
        offset: number;
        page_size: number;
        page_number: number;
    };
};

export interface ApiResponse<T> {
    message: string;
    success: boolean;
    data?: ApiResponseData<T>;
    secondary_data: NoseurObject<any>;
};

export type SanitizedResponse<T> = {
    sanitized: T;
    status: number;
    errors?: any[];
    errorMessage: string;
    apiResponse: ApiResponse<T>;
};

export class BaseService {

    transport: Ffs;
    interceptor = new Interceptor();
    baseUrl: string = process.env.REACT_APP_HERA_BASE_API_URL ?? "http://127.0.0.1:9401";
    cacheManager: KCacheManager<any> = IndexDbCacheManager.getInstance({ encryptor: LzEncryptor, bucket: "API_CACHE", storeName: "HERA_REQUESTS_CACHE" });

    constructor(baseUrl?: string) {
        this.transport = ffs.init({
            cache: false,
            promiscuous: true,
            interceptor: this.interceptor,
            cacheManager: this.cacheManager,
            baseUrl: baseUrl ?? this.baseUrl,
            cacheLifetime: 1000 * 60 * 60 * 24, // 24 hours
        });
        this.interceptor.registerPreRequest((config?: HttpConfig) => {
            const accessToken = CacheManager.get(CacheManager.ACCESS_TOKEN_KEY);
            config!.headers = {
                ...config?.headers,
                Authorization: `Bearer ${accessToken}`
            };
        });
    }

    normalizeParams(query: NoseurObject<any>) {
        if (!query.page) query.page = 1;
        if (!query.size) query.size = 10;
        if (!query.sort) query.sort = "id,DESC";
        if (!query.language) query.language = i18nManager.ActiveLanguage?.replaceAll("_", "-");
        Object.keys(query).forEach((key: string) => {
            const value = query[key];
            if (!TypeChecker.isArray(value)) return;
            query[key] = value.reduce((acc: string, v: string, index: number) => {
                if (index > 0) return `${acc},${key}=${v}`;
                return `${v}`;
            }, "");
        });
        return query;
    }

    report<T>(promise: Promise<any>, postResolve?: Function, postReject?: Function): Promise<SanitizedResponse<any>> {
        const resolver = (resolve: any, response: any) => {
            let transformedResponse = this.buildResponse(response);
            if (postResolve) postResolve(transformedResponse);
            resolve(transformedResponse);
        };
        return new Promise((resolve, reject) => {
            promise.then((response: T) => {
                resolver(resolve, response);
            }).catch((error: any) => {
                if (error.constructor?.name === "NoSufficientCacheSpaceLeftError") { // move as parameter to kyofuuc error to resolve as success
                    resolver(resolve, error.response);
                    return;
                }
                let transformedError = this.buildError(error);
                if (window.location.href.includes("/support/") && (error.response?.status === 401 || error.response?.status === 0)) {
                    this.transport.post((process.env.REACT_APP_LANUARIUS_BASE_API_URL ?? "http://127.0.0.1:9001") + "/janus/lanuarius/api/v1/user/auth/refresh", {
                        authenticating_platform: "omnehost",
                        access_token: CacheManager.get(CacheManager.ACCESS_TOKEN_KEY),
                        session_token: CacheManager.get(CacheManager.SESSION_TOKEN_KEY),
                    }).then((response) => {
                        CacheManager.insecurePut(CacheManager.ACCESS_TOKEN_KEY, response.data.data.access_token);
                        promise.then((response) => {
                            response.data = JSON.parse(response.data);
                            resolver(resolve, response);
                        }).catch(async (error: any) => {
                            transformedError = this.buildError(error);
                            if (postReject) postReject(transformedError);
                            reject(transformedError);
                        });
                    }).catch(() => {
                        if (!error?.config?.parsed?.pathname.endsWith("/count")) {
                            Utils.signIn();
                            return;
                        }
                        transformedError = this.buildError(error);
                        if (postReject) postReject(transformedError);
                        reject(transformedError);
                    });
                    return;
                }
                if (postReject) postReject(transformedError);
                reject(transformedError);
            });
        });
    }

    buildResponse<T>(response: any): SanitizedResponse<T> {
        if (!response.data || !response.data.data) return response;
        response.apiResponse = response.data;
        response.status = response?.status ?? 500;
        response.sanitized = response.apiResponse.data;
        return response;
    }

    buildError<T>(error: any): SanitizedResponse<T> {
        if (!error) error = {};
        error.message = error?.response?.data?.message ?? error?.response?.message ?? error?.message ?? "An error occur please try again later";
        error.errors = error?.response?.data?.errors;
        error.errorMessage = error.message;
        error.status = error.response?.status ?? -1;
        return error;
    }

    backToLogin(dialog?: AlertInterface) {
        dialog?.destroy();
        CacheManager.clear();
        window.location = "/" as any;
    }

}
